import omit from 'lodash/omit';

import { TprmRequirementObjectType } from '@evidentid/tprm-portal-lib/models/entity-details';

type RequirementValue = string | null | undefined;

interface AddressV1 {
    street_number: RequirementValue;
    street_name: RequirementValue;
    street_unit: RequirementValue;
    city: RequirementValue;
    state: RequirementValue;
    postal_code: RequirementValue;
    postal_code_suffix: RequirementValue;
    country: RequirementValue;
}

interface AddressV2 {
    street_address: RequirementValue;
    street_address_2: RequirementValue;
    city: RequirementValue;
    state: RequirementValue;
    postal_code: RequirementValue;
    country: RequirementValue;
}

interface TprmFieldAddress {
    country: RequirementValue;
    street: RequirementValue;
    aptSuiteOrUnit: RequirementValue;
    city: RequirementValue;
    state: RequirementValue;
    zipCode: RequirementValue;
}

interface CommonAddress extends TprmFieldAddress {
    streetNumber: RequirementValue;
    zipCodeSuffix: RequirementValue;
}

function addressV2ToCommon(v2: AddressV2): CommonAddress {
    return {
        streetNumber: '',
        street: v2.street_address,
        aptSuiteOrUnit: v2.street_address_2,
        city: v2.city,
        state: v2.state,
        zipCode: v2.postal_code,
        zipCodeSuffix: '',
        country: v2.country,
    };
}

function addressV1ToCommon(v1: AddressV1): CommonAddress {
    return {
        streetNumber: v1.street_number,
        street: v1.street_name,
        aptSuiteOrUnit: v1.street_unit,
        city: v1.city,
        state: v1.state,
        zipCode: v1.postal_code,
        zipCodeSuffix: v1.postal_code_suffix,
        country: v1.country,
    };
}

function parseCountry(address: CommonAddress, countryLabels?: Record<string, any>): CommonAddress {
    if (address.country) {
        const label = countryLabels?.[address.country]?.label;
        return { ...address, country: (label || address.country) as RequirementValue };
    }
    return address;
}

function coverageFieldAddressToCommon(address: TprmFieldAddress): CommonAddress {
    return {
        ...address,
        streetNumber: '',
        zipCodeSuffix: '',
    };
}

export function getCoverageAddressObjectString(
    addressObject?: Record<string, string | null | undefined>,
    countryLabels?: Record<string, any>,
): string {
    let addressString = '';
    let address = {} as CommonAddress;
    const coverageAddress = omit(addressObject, '$objectType') as unknown;

    if (addressObject) {
        switch (addressObject.$objectType) {
            case TprmRequirementObjectType.addressV2:
                address = addressV2ToCommon(coverageAddress as AddressV2);
                break;
            case TprmRequirementObjectType.coverageFieldAddress:
                address = coverageFieldAddressToCommon(coverageAddress as TprmFieldAddress);
                break;
            default:
                address = addressV1ToCommon(coverageAddress as AddressV1);
                break;
        }
        address = parseCountry(address, countryLabels);
        const { streetNumber, aptSuiteOrUnit, street, zipCode, zipCodeSuffix, city, state, country } = address;
        const streetNumberAndName = [ streetNumber, street ].filter(Boolean).join(' ');
        const postal = [ zipCode, zipCodeSuffix ].filter(Boolean).join('-');
        const stateAndPostal = [ state, postal ].filter(Boolean).join(' ');
        addressString = [ streetNumberAndName, aptSuiteOrUnit, city, stateAndPostal, country ].filter(Boolean).join(', ');
    }

    return addressString.trim();
}
